import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { auth, db } from "../../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { collection, getDocs } from "firebase/firestore";
import { httpsCallable, getFunctions } from "firebase/functions";
import { Icons } from "../../lib/Icons";

// Domain configuration
const DOMAINS = [
  { id: 'Communication', label: 'Communication', Icon: Icons.ChatBubble },
  { id: 'SEMH', label: 'Social & Emotional', Icon: Icons.Heart },
  { id: 'Cognition', label: 'Cognition & Learning', Icon: Icons.Brain },
  { id: 'Sensory/Physical', label: 'Sensory & Physical', Icon: Icons.Hand },
];

// Age ranges
const AGE_RANGES = [
  { id: 'early-years', label: 'Early Years (2-5)', min: 2, max: 5 },
  { id: 'primary', label: 'Primary (5-11)', min: 5, max: 11 },
  { id: 'secondary', label: 'Secondary (11-16)', min: 11, max: 16, hasSubOptions: true },
  { id: 'post-16', label: 'Post-16 (16+)', min: 16, max: 19 },
];

// Secondary sub-options for KS3/KS4 distinction
const SECONDARY_KEY_STAGES = [
  { id: 'ks3', label: 'Key Stage 3 (Years 7-9)', min: 11, max: 14, keyStage: 'ks3' },
  { id: 'ks4', label: 'Key Stage 4 (Years 10-11)', min: 14, max: 16, keyStage: 'ks4' },
  { id: 'secondary-all', label: 'All Secondary', min: 11, max: 16, keyStage: null },
];

const SoloSearch = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Filters
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [selectedDomain, setSelectedDomain] = useState(searchParams.get('domain') || '');
  const [selectedAgeRange, setSelectedAgeRange] = useState(searchParams.get('age') || '');
  const [selectedKeyStage, setSelectedKeyStage] = useState(searchParams.get('ks') || '');
  const [showInterventionsOnly, setShowInterventionsOnly] = useState(searchParams.get('interventions') === 'true');
  const [sortBy, setSortBy] = useState('relevance');

  // AI Search state
  const [aiSearching, setAiSearching] = useState(false);
  const [aiResults, setAiResults] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);

      // Subscription checks are not needed here - users can browse freely
      // Payment barrier is on activity detail pages

      // Load activities (available to all users, authenticated or not)
      await loadActivities();
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (selectedDomain) params.set('domain', selectedDomain);
    if (selectedAgeRange) params.set('age', selectedAgeRange);
    if (selectedKeyStage) params.set('ks', selectedKeyStage);
    if (showInterventionsOnly) params.set('interventions', 'true');
    setSearchParams(params, { replace: true });
  }, [searchQuery, selectedDomain, selectedAgeRange, selectedKeyStage, showInterventionsOnly, setSearchParams]);

  // Clear key stage selection when age range changes from secondary
  useEffect(() => {
    if (selectedAgeRange !== 'secondary') {
      setSelectedKeyStage('');
    }
  }, [selectedAgeRange]);

  const loadActivities = async () => {
    try {
      const kbRef = collection(db, 'kb_items');
      const snapshot = await getDocs(kbRef);
      
      const allActivities = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        allActivities.push({
          id: doc.id,
          ...data,
        });
      });

      setActivities(allActivities);
    } catch (error) {
      console.error('Error loading activities:', error);
    }
  };

  // Basic filtering logic
  const filteredActivities = useMemo(() => {
    let results = [...activities];

    // When not filtering by interventions, hide umbrella cards (show the 4 stages as separate results)
    if (!showInterventionsOnly) {
      results = results.filter(activity => !activity.interventionUmbrella);
    }

    // Text search (title, description, tags)
    if (searchQuery) {
      const query = searchQuery.toLowerCase().trim();
      results = results.filter(activity => {
        const titleMatch = activity.title?.toLowerCase().includes(query);
        const descMatch = activity.description?.toLowerCase().includes(query);
        const tagMatch = activity.tags?.some(tag => tag.toLowerCase().includes(query));
        const domainMatch = activity.targetDomains?.some(d => d.toLowerCase().includes(query));
        return titleMatch || descMatch || tagMatch || domainMatch;
      });
    }

    // Domain filter
    if (selectedDomain) {
      results = results.filter(activity => 
        activity.targetDomains?.includes(selectedDomain)
      );
    }

    // Age range filter
    if (selectedAgeRange) {
      const ageRange = AGE_RANGES.find(r => r.id === selectedAgeRange);
      if (ageRange) {
        results = results.filter(activity => {
          // First check explicit phase field
          if (activity.phase) {
            if (selectedAgeRange === 'early-years') {
              return activity.phase === 'eyfs' || activity.phase === 'both';
            } else if (selectedAgeRange === 'secondary') {
              return activity.phase === 'secondary' || activity.phase === 'both';
            } else if (selectedAgeRange === 'primary') {
              return activity.phase === 'primary' || activity.phase === 'both';
            }
          }
          
          // Stricter fallback: activity must be designed for the target age range
          // For early-years/EYFS (2-5), require maxAge <= 6 (designed for under 6s)
          // For primary (5-11), require maxAge <= 12 (designed for younger students)
          // For secondary (11-16), require minAge >= 10 (designed for older students)
          const actMin = activity.ageRange?.min || 0;
          const actMax = activity.ageRange?.max || 19;
          
          if (selectedAgeRange === 'early-years') {
            return actMax <= 6; // Must be designed for under 6s to show in EYFS
          } else if (selectedAgeRange === 'primary') {
            return actMax <= 12; // Must be designed for under 12s to show in primary
          } else if (selectedAgeRange === 'secondary') {
            return actMin >= 10; // Must be designed for 10+ to show in secondary
          }
          
          // For post-16, use overlap check
          return actMin <= ageRange.max && actMax >= ageRange.min;
        });
      }
    }

    // Key Stage filter (for secondary sub-options)
    if (selectedKeyStage && selectedKeyStage !== 'secondary-all') {
      const ksOption = SECONDARY_KEY_STAGES.find(ks => ks.id === selectedKeyStage);
      if (ksOption && ksOption.keyStage) {
        results = results.filter(activity => {
          // Check if activity has keyStages field and includes this key stage
          if (activity.keyStages && Array.isArray(activity.keyStages)) {
            return activity.keyStages.includes(ksOption.keyStage);
          }
          // Fallback to age range if no keyStages field
          const actMin = activity.ageRange?.min || 0;
          const actMax = activity.ageRange?.max || 19;
          return actMin <= ksOption.max && actMax >= ksOption.min;
        });
      }
    }

    // Interventions filter: show umbrella + other interventions, hide child stages
    if (showInterventionsOnly) {
      results = results.filter(activity => activity.isIntervention === true && !activity.parentInterventionSlug);
    }

    // Sorting
    if (sortBy === 'title') {
      results.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    } else if (sortBy === 'newest') {
      results.sort((a, b) => {
        const aDate = a.createdAt?.toDate?.() || new Date(0);
        const bDate = b.createdAt?.toDate?.() || new Date(0);
        return bDate - aDate;
      });
    }

    return results;
  }, [activities, searchQuery, selectedDomain, selectedAgeRange, selectedKeyStage, showInterventionsOnly, sortBy]);

  // AI-powered search
  const handleAISearch = async () => {
    if (!searchQuery.trim()) return;

    setAiSearching(true);
    try {
      const functions = getFunctions(undefined, 'europe-west3');
      const aiSearch = httpsCallable(functions, 'soloAISearchFunc');
      const result = await aiSearch({ query: searchQuery });
      setAiResults(result.data.results);
    } catch (error) {
      console.error('AI search error:', error);
      // Fall back to basic search
    } finally {
      setAiSearching(false);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedDomain('');
    setSelectedAgeRange('');
    setSelectedKeyStage('');
    setShowInterventionsOnly(false);
    setAiResults(null);
  };

  const hasActiveFilters = searchQuery || selectedDomain || selectedAgeRange || selectedKeyStage || showInterventionsOnly;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500/20 border-t-purple-500 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/80 backdrop-blur-lg border-b border-slate-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/solo/dashboard" className="flex items-center gap-3">
              <img 
                src="/brand/logo-thumbnail-purple.png"
                alt="SEN-Ed" 
                className="w-10 h-10 rounded-xl"
              />
              <span className="text-xl font-bold text-white">SEN-Ed</span>
            </Link>

            <Link 
              to="/solo/dashboard" 
              className="text-slate-400 hover:text-white transition-colors"
            >
              ← Back to Dashboard
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative max-w-2xl mx-auto">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search resources..."
              className="w-full px-6 py-4 pl-14 bg-slate-800 text-white placeholder-slate-400 rounded-2xl border border-slate-700 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none text-lg"
            />
            <svg className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            
            {searchQuery && (
              <button
                onClick={handleAISearch}
                disabled={aiSearching}
                className="absolute right-3 top-1/2 -translate-y-1/2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {aiSearching ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Searching...
                  </>
                ) : (
                  <>
                    <Icons.Sparkles className="w-4 h-4" />
                    AI Search
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="bg-slate-800 rounded-2xl p-6 sticky top-24">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-white">Filters</h3>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-purple-400 hover:text-purple-300"
                  >
                    Clear all
                  </button>
                )}
              </div>

              {/* Domain Filter */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-300 mb-3">Domain</label>
                <div className="space-y-2">
                  {DOMAINS.map(domain => (
                    <button
                      key={domain.id}
                      onClick={() => setSelectedDomain(selectedDomain === domain.id ? '' : domain.id)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all ${
                        selectedDomain === domain.id
                          ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                          : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <domain.Icon className="w-4 h-4" />
                      <span className="text-sm">{domain.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Age Range Filter */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-300 mb-3">Age Range</label>
                <div className="space-y-2">
                  {AGE_RANGES.map(range => (
                    <div key={range.id}>
                      <button
                        onClick={() => setSelectedAgeRange(selectedAgeRange === range.id ? '' : range.id)}
                        className={`w-full text-left px-4 py-2.5 rounded-xl text-sm transition-all ${
                          selectedAgeRange === range.id
                            ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                            : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                        }`}
                      >
                        {range.label}
                      </button>
                      
                      {/* Show Key Stage sub-options when Secondary is selected */}
                      {range.hasSubOptions && selectedAgeRange === range.id && (
                        <div className="mt-2 ml-4 space-y-1.5 border-l-2 border-purple-500/30 pl-3">
                          {SECONDARY_KEY_STAGES.map(ks => (
                            <button
                              key={ks.id}
                              onClick={() => setSelectedKeyStage(selectedKeyStage === ks.id ? '' : ks.id)}
                              className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-all ${
                                selectedKeyStage === ks.id
                                  ? 'bg-pink-500/20 text-pink-300 border border-pink-500/30'
                                  : 'bg-slate-600/50 text-slate-400 hover:bg-slate-600'
                              }`}
                            >
                              {ks.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Interventions Toggle */}
              <div className="mb-6">
                <button
                  onClick={() => setShowInterventionsOnly(!showInterventionsOnly)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    showInterventionsOnly
                      ? 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-300 border border-amber-500/30'
                      : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  <Icons.Target className={`w-5 h-5 ${showInterventionsOnly ? 'text-amber-400' : 'text-slate-400'}`} />
                  <div className="flex-1 text-left">
                    <span className="text-sm font-medium">Interventions Only</span>
                    {showInterventionsOnly && (
                      <p className="text-xs text-amber-400/70 mt-0.5">Showing evidence-based interventions</p>
                    )}
                  </div>
                  <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-all ${
                    showInterventionsOnly ? 'bg-amber-500' : 'bg-slate-600'
                  }`}>
                    {showInterventionsOnly && <Icons.Check className="w-3 h-3 text-white" />}
                  </div>
                </button>
              </div>

              {/* Sort */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-3">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-700 text-slate-300 rounded-xl border border-slate-600 focus:border-purple-500 focus:outline-none"
                >
                  <option value="relevance">Relevance</option>
                  <option value="title">Title A-Z</option>
                  <option value="newest">Newest First</option>
                </select>
              </div>
            </div>
          </aside>

          {/* Results */}
          <main className="flex-1">
            {/* Results header */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-slate-400">
                {filteredActivities.length} {filteredActivities.length === 1 ? 'result' : 'results'}
                {searchQuery && <span> for "{searchQuery}"</span>}
              </p>
            </div>

            {/* AI Results Banner */}
            {aiResults && (
              <div className="mb-6 p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-2xl border border-purple-500/20">
                <div className="flex items-center gap-3 mb-2">
                  <Icons.Sparkles className="w-5 h-5 text-purple-400" />
                  <h4 className="text-white font-medium">AI-Powered Suggestions</h4>
                </div>
                <p className="text-slate-300 text-sm">
                  Based on your search, we recommend these resources that may not match exactly but are highly relevant.
                </p>
              </div>
            )}

            {/* Results Grid */}
            {filteredActivities.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-slate-700/50 flex items-center justify-center">
                  <Icons.MagnifyingGlass className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">No resources found</h3>
                <p className="text-slate-400 mb-6">
                  Try adjusting your search or filters
                </p>
                <button
                  onClick={clearFilters}
                  className="px-6 py-3 bg-purple-500 text-white rounded-xl hover:bg-purple-600 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredActivities.map(activity => (
                  <Link
                    key={activity.id}
                    to={`/solo/activity/${activity.id}`}
                    className="group bg-slate-800 rounded-2xl overflow-hidden hover:ring-2 hover:ring-purple-500/50 transition-all"
                  >
                    {/* Header */}
                    <div className="h-24 bg-gradient-to-br from-purple-600/20 to-pink-600/20 p-4 flex items-end">
                      <div className="flex gap-2 flex-wrap">
                        {activity.targetDomains?.slice(0, 2).map(domain => (
                          <span key={domain} className="px-2 py-1 bg-white/10 backdrop-blur-sm rounded-full text-xs text-white">
                            {domain}
                          </span>
                        ))}
                        {/* Single age/phase pill: key stages (UK) when present, else age range */}
                        {(activity.keyStages?.length > 0 || activity.ageRange) && (
                          <span className="px-2 py-1 bg-pink-500/20 backdrop-blur-sm rounded-full text-xs text-pink-300 border border-pink-500/30">
                            {activity.keyStages?.length > 0
                              ? activity.keyStages.map(ks => ks.toUpperCase()).join(' / ')
                              : `Ages ${activity.ageRange.min}-${activity.ageRange.max}`}
                          </span>
                        )}
                        {activity.phase === 'secondary' && (
                          <span className="px-2 py-1 bg-purple-500/20 backdrop-blur-sm rounded-full text-xs text-purple-300 border border-purple-500/30">
                            Secondary
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="p-5">
                      <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-purple-400 transition-colors">
                        {activity.title}
                      </h3>
                      <p className="text-slate-400 text-sm line-clamp-2 mb-4">
                        {activity.description}
                      </p>
                      
                      {/* Tags */}
                      {activity.tags && activity.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {activity.tags.slice(0, 4).map((tag, idx) => (
                            <span 
                              key={idx}
                              className="px-2 py-0.5 bg-slate-700 rounded text-xs text-slate-400"
                            >
                              {tag}
                            </span>
                          ))}
                          {activity.tags.length > 4 && (
                            <span className="px-2 py-0.5 text-xs text-slate-500">
                              +{activity.tags.length - 4} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default SoloSearch;


